import { creatorbase } from '@round/api';
import { uniq, uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useInfiniteReports, useUsersQueriesByIds } from '@round/creatorbase-helpers/api-hooks';
import { isNumber } from 'utility/utility';

export default function useReports(params: Omit<creatorbase.GetReportsParams, 'page'>) {
    const reportsQuery = useInfiniteReports(params, { enabled: isNumber(params.client_id) });

    const reports = useMemo(() => reportsQuery.data?.pages.flatMap((page) => page.results) ?? [], [reportsQuery.data]);

    const assigneeIdsByPage = useMemo(
        () => reportsQuery.data?.pages.map((page) => uniq(page.results.flatMap((r) => r.assignee_ids))) ?? [],
        [reportsQuery.data]
    );

    const usersQueries = useUsersQueriesByIds(assigneeIdsByPage);
    const assignees = useMemo(
        () =>
            uniqBy(
                usersQueries.flatMap((q) => q.data?.results ?? []),
                (u) => u.id
            ),
        [usersQueries]
    );

    const reportIdToPageIndex = useMemo(() => {
        const map = new Map<creatorbase.Report['id'], number>();
        reportsQuery.data?.pages.forEach((page, pageIndex) => {
            page.results.forEach((report) => map.set(report.id, pageIndex));
        });
        return map;
    }, [reportsQuery.data]);

    const isAssigneesLoading = useCallback(
        (reportId: creatorbase.Report['id']) => {
            const pageIndex = reportIdToPageIndex.get(reportId);
            if (pageIndex === undefined) {
                return false;
            }
            return usersQueries[pageIndex]?.isLoading ?? false;
        },
        [reportIdToPageIndex, usersQueries]
    );

    const data = useMemo(
        () => (reportsQuery.data ? { reports, assignees } : null),
        [reportsQuery.data, reports, assignees]
    );

    return {
        data,
        isPending: reportsQuery.isPending,
        isFetching: reportsQuery.isFetching,
        isAssigneesLoading,
        errorMessage: reportsQuery.errorMessage,
        isFetchingNextPage: reportsQuery.isFetchingNextPage,
        hasNextPage: reportsQuery.hasNextPage,
        fetchNextPage: reportsQuery.fetchNextPage,
    };
}
